import { createBigDecimal } from '@0xtorch/big-decimal'
import { z } from 'zod'

export const coinInfoSchema = z.object({
  /** e.g. "BTC" */
  coin: z.string(),
  /** e.g. true */
  depositAllEnable: z.boolean(),
  /** e.g. "0.08074558" */
  free: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "0.00000000" */
  freeze: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "0.00000000" */
  ipoable: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "0.00000000" */
  ipoing: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. false */
  isLegalMoney: z.boolean(),
  /** e.g. "0.00000000" */
  locked: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "Bitcoin" */
  name: z.string(),
})

export const fundingAssetSchema = z.object({
  /** e.g. "USDT" */
  asset: z.string(),
  /** avalible balance | "1" */
  free: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** locked asset | e.g. "0" */
  locked: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** freeze asset | e.g. "0" */
  freeze: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "0" */
  withdrawing: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "0.00000091" */
  btcValuation: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
})

export const futureAssetSchema = z.object({
  /** unique account code | e.g. "SgsR" */
  accountAlias: z.string(),
  /** asset name | e.g. "USDT" */
  asset: z.string(),
  /** wallet balance | e.g. "122607.35137903" */
  balance: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** crossed wallet balance | e.g. "23.72469206" */
  crossWalletBalance: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** unrealized profit of crossed positions | e.g. "0.00000000" */
  crossUnPnl: z
    .string()
    .regex(/^\-?\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** available balance | e.g. "23.72469206" */
  availableBalance: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  // /** maximum amount for transfer out | e.g. "23.72469206" */
  // maxWithdrawAmount: z
  //   .string()
  //   .regex(/^\d+(\.\d+)?$/)
  //   .transform((v) => createBigDecimal(v)),
  // /** whether the asset can be used as margin in Multi-Assets mode | e.g. true */
  // marginAvailable: z.boolean(),
  /** e.g. 1617939110373 */
  updateTime: z.number().int(),
})

export const marginAssetSchema = z.object({
  /** e.g. "BTC" */
  asset: z.string(),
  /** e.g. "0.00000000" */
  borrowed: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "0.00499500" */
  free: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "0.00000000" */
  interest: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "0.00000000" */
  locked: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "0.00499500" */
  netAsset: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
})

export const simpleEarnFlexiblePositionSchema = z.object({
  /** e.g. "75.46000000" */
  totalAmount: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "USDT" */
  asset: z.string(),
  /** e.g. "BETH" */
  airDropAsset: z.string().optional(),
  /** e.g. "232.23123213" */
  collateralAmount: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "USDT001" */
  productId: z.string(),
  /** e.g. "0.22759183" */
  cumulativeBonusRewards: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "0.22759183" */
  cumulativeRealTimeRewards: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "0.45459183" */
  cumulativeTotalRewards: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
})

export const simpleEarnLockedPositionSchema = z.object({
  /** e.g. "123123" */
  positionId: z.string(),
  /** e.g. "Axs*90" */
  projectId: z.string(),
  /** e.g. "AXS" */
  asset: z.string(),
  /** e.g. "122.09202928" */
  amount: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .transform((v) => createBigDecimal(v)),
  /** e.g. "1646182276000" */
  purchaseTime: z.string(),
  /** e.g. "60" */
  duration: z.string(),
  /** e.g. "4" */
  accrualDays: z.string(),
  /** e.g. "AXS" */
  rewardAsset: z.string(),
  /** e.g. "0.23" */
  APY: z.string(),
  /** e.g. true */
  isRenewable: z.boolean(),
  /** e.g. true */
  isAutoRenew: z.boolean(),
  /** e.g. "1732182276000" */
  redeemDate: z.string(),
})
